import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import ModalCloseButton from './ModalCloseButton';
import './ConfirmationModal.css';

function ConfirmationModal({
  isOpen,
  entityName,
  entityType = 'item',
  action = 'delete', // delete, disable, enable, archive, etc.
  onConfirm,
  onCancel,
  isLoading,
  customMessage, // Optional custom warning / detail message
  primaryMessage, // Optional primary body message (used especially by info variant)
  actionVerb, // Optional custom action verb (for resend actions like "resend the verification email to")
  title, // Optional custom modal title
  size = 'small', // small | medium
  variant = 'confirm', // confirm | info
  children, // Optional extra body content (e.g. required reason textarea)
  confirmDisabled = false,
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const capitalizedType = entityType.charAt(0).toUpperCase() + entityType.slice(1);
  const capitalizedAction = action.charAt(0).toUpperCase() + action.slice(1);
  const modalTitle = title || `${capitalizedAction} ${capitalizedType}`;
  const isInfoVariant = variant === 'info';

  // Determine message based on action type
  const getActionMessage = () => {
    if (primaryMessage) return primaryMessage;

    if (isInfoVariant) {
      return (
        <>
          <strong>{entityName}</strong> cannot be permanently deleted.
        </>
      );
    }

    const getActionVerb = () => {
      // Use custom actionVerb if provided (for dynamic messages like verification email vs reactivation link)
      if (actionVerb) return actionVerb;

      // Default: return action as-is (e.g., 'delete' returns 'delete', 'disable' returns 'disable')
      return action;
    };

    return (
      <>
        Are you sure you want to {getActionVerb()} <strong>{entityName}</strong>?
      </>
    );
  };

  const getWarningMessage = () => {
    if (customMessage) return customMessage;

    const lowerType = entityType.toLowerCase();

    switch (action) {
      case 'delete':
        return 'This action cannot be undone. All associated data will be permanently removed.';
      case 'disable':
        return `This will prevent the ${lowerType} from being used. You can enable it again later.`;
      case 'enable':
        return `This will make the ${lowerType} available for use.`;
      case 'archive':
        return `This will archive the ${lowerType}. You can restore it later if needed.`;
      case 'deactivate':
        return `This will deactivate the ${lowerType}. You can reactivate it later.`;
      case 'activate':
        return `This will activate the ${lowerType} and make it available.`;
      case 'resend':
        return `This will resend the ${lowerType}. The previous one will be invalidated.`;
      default:
        return '';
    }
  };

  // Determine button color based on action severity
  const getButtonClass = () => {
    switch (action) {
      case 'delete':
        return 'action-danger';
      case 'disable':
      case 'deactivate':
      case 'archive':
        return 'action-warning';
      case 'enable':
      case 'activate':
      case 'resend':
        return 'action-primary';
      default:
        return 'action-danger';
    }
  };

  const buttonText = isLoading
    ? `${capitalizedAction} ${capitalizedType}...`
    : `${capitalizedAction} ${capitalizedType}`;

  return ReactDOM.createPortal(
    <div className="confirmation-modal-overlay">
      <div className={`confirmation-modal confirmation-modal-${size}${isInfoVariant ? ' confirmation-modal-info' : ''}`}>
        <div className="modal-header">
          <h2>{modalTitle}</h2>
          <ModalCloseButton onClick={onCancel} disabled={isLoading} title="Close" />
        </div>

        <div className="modal-body">
          <div className="confirmation-icon">{isInfoVariant ? 'ℹ️' : '⚠️'}</div>
          <div className="confirmation-message">
            {getActionMessage()}
          </div>
          {getWarningMessage() && (
            <p className="confirmation-warning">
              {getWarningMessage()}
            </p>
          )}
          {children}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`action-btn ${getButtonClass()}`}
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmationModal;
